import { useCallback, useEffect, useMemo, useState } from 'react';

import { OptionType } from '@evinced-private/ui-common';

import { LABEL_DELIMITER } from 'src/common/consts/platformConfigConsts';
import {
	ConfiguredValues,
	markOptionsWithWarningsFromKeyValue
} from 'src/common/helpers/ConfigurationWarningsHelper';
import { ConfigurationLabel } from 'src/common/types/ConfigurationTypes';

import { LabelChangeForUpdate } from '../api/helpers/LabelChangeHelpers';
import {
	applyWarningsToValueOptions,
	buildDisplayNameToKeyMap,
	extractLabelKey,
	parseLabel,
	TestRowSharedType,
	transformLabelValuesToOptions
} from '../helpers';
import { extractExistingLabelsForUpdate } from '../update-tests-modal/helpers/UpdateTestsModalHelpers';

interface UseUpdateLabelsManagementProps<T extends TestRowSharedType> {
	testRows: T[];
	tenantConfiguredLabels: ConfigurationLabel[];
	configuredValues: ConfiguredValues;
}

interface UseUpdateLabelsManagementReturn {
	selectedLabel: OptionType[];
	setSelectedLabel: (label: OptionType[]) => void;
	selectedNewValue: OptionType[];
	setSelectedNewValue: (value: OptionType[]) => void;
	changes: LabelChangeForUpdate[];
	labelOptions: OptionType[];
	valueOptions: OptionType[];
	removeChange: (index: number) => void;
}

export const useUpdateLabelsManagement = <T extends TestRowSharedType>({
	testRows,
	tenantConfiguredLabels,
	configuredValues
}: UseUpdateLabelsManagementProps<T>): UseUpdateLabelsManagementReturn => {
	const [selectedLabel, setSelectedLabel] = useState<OptionType[]>([]);
	const [selectedNewValue, setSelectedNewValue] = useState<OptionType[]>([]);
	const [changes, setChanges] = useState<LabelChangeForUpdate[]>([]);

	const labelOptions = useMemo((): OptionType[] => {
		const options = extractExistingLabelsForUpdate(testRows, tenantConfiguredLabels);
		const displayNameMap = buildDisplayNameToKeyMap(tenantConfiguredLabels);

		return markOptionsWithWarningsFromKeyValue(
			options,
			configuredValues,
			(label) => extractLabelKey(label, displayNameMap),
			(label) => parseLabel(label, displayNameMap)
		);
	}, [testRows, tenantConfiguredLabels, configuredValues]);

	// Get available values for the selected label
	const valueOptions = useMemo((): OptionType[] => {
		if (selectedLabel.length === 0) {
			return [];
		}
		const selectedLabelValue = selectedLabel[0].value as string;
		const displayNameMap = buildDisplayNameToKeyMap(tenantConfiguredLabels);
		const labelKey = extractLabelKey(selectedLabelValue, displayNameMap);

		if (!labelKey) {
			return [];
		}
		const options = transformLabelValuesToOptions(tenantConfiguredLabels, labelKey);

		return applyWarningsToValueOptions(options, labelKey, configuredValues);
	}, [tenantConfiguredLabels, selectedLabel, configuredValues]);

	// Auto-add change when both label and new value are selected
	useEffect(() => {
		if (selectedLabel.length > 0 && selectedNewValue.length > 0) {
			const oldLabelValue = selectedLabel[0].value as string;
			const newValue = selectedNewValue[0].value as string;
			const parts = oldLabelValue.split(LABEL_DELIMITER);
			const labelKey = parts[0];
			const newLabelValue = `${labelKey}:${newValue}`;

			const newChange: LabelChangeForUpdate = {
				oldLabelValue,
				newLabelValue
			};

			setChanges((prevChanges) => {
				// Check if this label already has a change
				const existingIndex = prevChanges.findIndex(
					(change) => change.oldLabelValue.toLowerCase() === oldLabelValue.toLowerCase()
				);
				if (existingIndex >= 0) {
					// Update existing change
					const updated = [...prevChanges];
					updated[existingIndex] = newChange;
					return updated;
				}
				// Add new change
				return [...prevChanges, newChange];
			});

			// Reset selections for next change
			setSelectedLabel([]);
			setSelectedNewValue([]);
		}
	}, [selectedLabel, selectedNewValue]);

	const removeChange = useCallback((index: number) => {
		setChanges((prevChanges) => prevChanges.filter((_, i) => i !== index));
	}, []);

	return {
		selectedLabel,
		setSelectedLabel,
		selectedNewValue,
		setSelectedNewValue,
		changes,
		labelOptions,
		valueOptions,
		removeChange
	};
};
